// Margin, price for a target margin, and labor variance (PRD §3.6–3.7).
// Reimbursable expenses add equal revenue and cost, so callers leave them out of both.

export const DEFAULT_TARGET_MARGIN = 0.3;

export interface MarginInputs {
  revenue: number;
  labor: number;
  expenses: number;
  /** Fully burdened view only: overhead charged per hour of labor. */
  overheadPerHour?: number;
  hours?: number;
}

export interface Margin {
  contribution: number;
  contributionPct: number;
  burdened: number;
  burdenedPct: number;
}

export function margin(i: MarginInputs): Margin {
  const contribution = i.revenue - i.labor - i.expenses;
  const burdened = contribution - (i.overheadPerHour ?? 0) * (i.hours ?? 0);
  const pct = (x: number) => (i.revenue !== 0 ? x / i.revenue : 0);
  return { contribution, contributionPct: pct(contribution), burdened, burdenedPct: pct(burdened) };
}

/** The price at which total planned cost earns exactly the target margin. */
export function priceForTarget(totalCost: number, target = DEFAULT_TARGET_MARGIN): number {
  if (target >= 1) throw new RangeError("A target margin must be below 100%.");
  return totalCost / (1 - target);
}

export interface LaborVariance {
  /** (actual hours − plan hours) × plan rate. */
  volume: number;
  /** (actual rate − plan rate) × actual hours: "hit the hours, wrong person". */
  rateMix: number;
  total: number;
}

export function laborVariance(planHours: number, planRate: number, actualHours: number, actualRate: number): LaborVariance {
  const volume = (actualHours - planHours) * planRate;
  const rateMix = (actualRate - planRate) * actualHours;
  return { volume, rateMix, total: volume + rateMix };
}
